<template>
  <ion-page id="legacy-rep-tree-page">
    <ion-content>

    <div v-show="showTour">
       <Tour  :tour="tourInfo" :tourTrigger="tourTrigger" @tour-done="tourDone" />
    </div>

    <div id="the_comment" :style="styleComment">
      <p>{{ commentString }}</p>
    </div>

    <div id="the_board" :style="styleBoard">
      <CMChessboard :fen="fen" :orientation="orientation"
              :boardSize="boardSize"
              :arrows="arrowPairs" @board-move="boardMove" />
    </div>

    <div id="the_tree" :style="styleRepTree">
      <LegacyRepTree :moveDirection="directionTrigger" calledFrom="tree" :reloadRepertoire="reloadTrigger"
               :showComments="showComments"
              @move-click="moveClick" @branch-click="branchClick" @repertoire-comment="updateComment"
              @repertoire-info="repertoireInfo" @toolbar-update="toolbarUpdate" />
    </div>

    </ion-content>
    <Toolbar id="the_toolbar" :ignoreClicks="showTour" :elements="toolbarElements" 
                    :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />

  </ion-page>
  
</template>

<script setup>

  import { IonItem, IonList, IonButton, IonRange,IonPage, IonContent, modalController } from '@ionic/vue';

  import LegacyRepTree from '../../components/LegacyRepTree.vue';

  import ModalLegacyRepSelector from '@/components/ModalLegacyRepSelector.vue'
  import { t } from '@/composables/translateText.js'

  import Toolbar from '@/components/Toolbar.vue'
  import CMChessboard from '@/components/CMChessboard.vue'
  import { doStartupCheck } from '@/composables/startupCheck.js'
  import ModalErrorMessage from '@/components/ModalErrorMessage.vue'

  import { ref, watch } from 'vue'
  import { onMounted, onBeforeMount } from 'vue'

  import { useConnectionStore } from '@/stores/connectionStore';
  import { useGetData } from '@/composables/getData.js'
  import { useWindowSizeStore } from '@/stores/windowSizeStore';
  import { availableWidthHeight, calculateChessBoardRect, isScreenPortraitOrientation } from '@/composables/screenSize'
  import Tour from '@/components/Tour.vue'
  import { findToolbarElement, setToolbarElementDisabledFlag, setToolbarElementLabel} 
                from '@/composables/toolbarSupport.js'

  const INITIAL_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
  const slotId = ref("edit")
  const reloadTrigger = ref(0)
  
  const connectionStore = useConnectionStore()

  const isDark = ref(connectionStore.getDarkmode != "light")
  const menuTrigger = ref(0)
  const tourTrigger = ref(0)
  const tourOption = connectionStore.getTourOption
  const showTour = ref((tourOption == "show"))
  
  const COMMENT_FONTSIZE = 16
  const COMMENT_HEIGHT = COMMENT_FONTSIZE * 1.2 * 2 + 4
  
  const commentString = ref("Retrieving repertoire")

  var directionTriggerCounter = 1
  const directionTrigger = ref(1)

  //console.log("############################### in reptree")
  const TOOLBAR_BACK = 0
  const TOOLBAR_FORWARD = 1
  const TOOLBAR_COMMENTS = 2
  const TOOLBAR_REPERTOIRE = 3
  const TOOLBAR_TOUR = 4
  const TOOLBAR_DONE = 5

  const toolbarElements = ref([
    { index : TOOLBAR_BACK, label : "<" ,disabled : true },
    { index : TOOLBAR_FORWARD, label : ">" ,disabled : true },
    { index : TOOLBAR_COMMENTS, label : t("Hide Comments") ,disabled : false },
    { index : TOOLBAR_REPERTOIRE, label : t("Repertoire") , disabled : false, collapse:true},
  ])

  const SHOW_TREE = 0
  const SHOW_HELP = 1
  const SHOW_REPERTOIRE_LIST = 2
  const SHOW_SUBSCRIBERS_ONLY = 3

  const showState = ref(SHOW_TREE)
  const showComments = ref(true)

  const toolbarTrigger = ref(0)

  const repertoireName = ref("")
  var blankAppendTrigger = " "
  const orientation = ref("white")
  const fen = ref(INITIAL_FEN)
  const arrowPairs = ref([])

  const windowSizeStore = useWindowSizeStore()
  const windowSizeRef = ref({ width : windowSizeStore.getWindowSize['width'],
                        height : windowSizeStore.getWindowSize['height'] })
  
  //console.log("**** explorer initial screensize", windowSizeRef.value)

  const boardSize = ref(300)

  // tour stuff
  const tourInfo = ref({
    id : "repertoire_tree",
    video : "https://www.chessvis.com/videos/repertoires_on_web.mp4",
    elements : [
      { id: "", title: t("Repertoire Tree Viewer"), text : t("This is a '<i>Bird's Eye View</i>' of your repertoire'.<br><br>As you navigate around the tree, the board will be updated to reflect the moves to that point.<br><br>The video is for the entire set of repertoire routines.")},
      { id: "the_tree", title: t("The Tree"), text : t("Moves that have a <b>(+)</b> after them represent places where there are multiple move choices. Tapping the <b>(+)</b> will open up the branches and highlight the choices on the board and in the tree.")},
      { id: "the_comment", title: t("Move Comments"), text : t("If there is a comment associated with the move, it will be displayed here.")},
      { id: "the_toolbar", title: t("Hide Comments - Toolbar"), text : t("By default, comments are shown within the tree text.<br><br>If you find that distracting, or want a more compact 'tree' then this will toggle the comments out of and back into view.")},
      { id: "the_toolbar", title: t("Repertoire - Toolbar"), text : t("This will bring up the list of repertoires so you can select a different one.")},
    ]
  })

  const styleRepTree = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleBoard = ref({
    position : "absolute",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleComment = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px",
    "font-size" : COMMENT_FONTSIZE + "px",
    "text-align" : "center",
    "line-height" : "1.2em"
  })


  function afterStartupCallbackRoutine(stuffChanged) {

    // this can be used to switch away from non-subscribers
    if (connectionStore.getIsSubscriber != 1) {
      showState.value = SHOW_SUBSCRIBERS_ONLY
      return
    }
    updateWidthHeights()
    menuTrigger.value++
  }

  doStartupCheck(false,afterStartupCallbackRoutine)

  // Repertoire Selector Modal
  const repertoireSelectorModal = async () => {
    const modal = await modalController.create({
      component: ModalLegacyRepSelector,
      componentProps: {
        descriptionHeading : t('Select the Repertoire to view') ,
        whoCalled : "viewer"
      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm') {
      //console.log("Explorer Options confirm",data)
      if (data.changed)
        repertoireDone(data.changed)
    }
  };

  function updateComment(comment) {
    commentString.value = comment
  }

  function incrementAppendTrigger() {
    if (blankAppendTrigger.length > 64)
      blankAppendTrigger = ""
    blankAppendTrigger += " "
  }

  function repertoireInfo(playingColorName,repertoireName) {
    //console.log("repertoire info coming back",playingColorName,repertoireName)
    commentString.value = repertoireName

    incrementAppendTrigger()
    orientation.value = playingColorName + blankAppendTrigger
  }

  function updateStyleObjects(boundaries) {
    boardSize.value = boundaries['board'].height

    styleBoard.value.height = boardSize.value + "px"
    styleBoard.value.height = boundaries['board'].height + "px"
    styleBoard.value.width = boundaries['board'].width + "px"

    styleBoard.value.top = boundaries['board'].top + "px"
    styleBoard.value.left = boundaries['board'].left + "px"

    styleRepTree.value.height = boundaries['tree'].height + "px"
    styleRepTree.value.width = boundaries['tree'].width + "px"
    styleRepTree.value.top = boundaries['tree'].top + "px"
    styleRepTree.value.left = boundaries['tree'].left + "px"

    styleComment.value.height = boundaries['comment'].height + "px"
    styleComment.value.width = boundaries['comment'].width + "px"
    styleComment.value.top = boundaries['comment'].top + "px"
    styleComment.value.left = boundaries['comment'].left + "px"

    //console.log("**** boardsize ",boardSize.value)
  }

  function updateWidthHeights() {

    const availableSpace = availableWidthHeight()
    const isPortrait = isScreenPortraitOrientation(availableSpace)
    const boundaries = (isPortrait) ? calculatePortraitWidthHeights(availableSpace) :
                                      calculateLandscapeWidthHeights(availableSpace)
    
    updateStyleObjects(boundaries)
   
  }

  function calculatePortraitWidthHeights(availableSpace) {

    // reserve space for the instruction lines
    const availableSpaceForBoard = { width : availableSpace.width ,
                height : availableSpace.height / 2,
                isPortrait : true }

    const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)
    
    const boardHeight = boardDimensions.height
    const boardWidth = boardDimensions.width
    const boardPositionTop = boardDimensions.y
    const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

    const commentPositionTop = boardPositionTop + boardHeight
    const commentPositionLeft = availableSpace.leftRightIndent
    const commentWidth = availableSpace.width - availableSpace.leftRightIndent * 2

    const treePositionTop = commentPositionTop + COMMENT_HEIGHT
    const treePositionLeft = availableSpace.leftRightIndent
    const treeHeight = availableSpace.height - treePositionTop
    const treeWidth = commentWidth

    const result = { board : {
        width : boardWidth,
        height : boardHeight,
        left : boardPositionLeft + availableSpace.leftIndent,
        top : boardPositionTop + availableSpace.topIndent
      },
      tree : {
        width : treeWidth,
        height : treeHeight,
        left : treePositionLeft + availableSpace.leftIndent,
        top : treePositionTop + availableSpace.topIndent
      },
      comment : {
        width : commentWidth,
        height : COMMENT_HEIGHT,
        left : commentPositionLeft + availableSpace.leftIndent,
        top : commentPositionTop + availableSpace.topIndent
      }
    }
    //console.log("board and movelist dimensions",result)
    return (result)
  }

  function calculateLandscapeWidthHeights(availableSpace) {

    // reserve space for the tree
    const availableSpaceForBoard = { width : availableSpace.width / 2,
                height : availableSpace.height - COMMENT_HEIGHT,
                isPortrait : false }

    const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)
    const commentPositionTop = 0
    const commentPositionLeft = availableSpace.leftRightIndent
    const commentWidth = availableSpace.width - availableSpace.leftRightIndent * 2

    const boardHeight = boardDimensions.height
    const boardWidth = boardDimensions.width
    const boardPositionTop = boardDimensions.y + COMMENT_HEIGHT
    const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

    const treePositionTop = COMMENT_HEIGHT
    const treePositionLeft = boardPositionLeft + boardWidth + availableSpace.leftRightIndent
    const treeHeight = availableSpace.height - COMMENT_HEIGHT
    const treeWidth = availableSpace.width - treePositionLeft

    
    const result = { board : {
        width : boardWidth,
        height : boardHeight,
        left : boardPositionLeft + availableSpace.leftIndent,
        top : boardPositionTop + availableSpace.topIndent
      },
      tree : {
        width : treeWidth,
        height : treeHeight,
        left : treePositionLeft + availableSpace.leftIndent,
        top : treePositionTop + availableSpace.topIndent
      },
      comment : {
        width : commentWidth,
        height : COMMENT_HEIGHT,
        left : commentPositionLeft + availableSpace.leftIndent,
        top : commentPositionTop + availableSpace.topIndent
      }
    }
    //console.log("board and movelist dimensions",result)
    return (result)
  }

  watch(() => windowSizeStore.getWindowSize, () => {
    //console.log('*********** REP TREE -- window size changes detected')
    windowSizeRef.value = { width : windowSizeStore.getWindowSize['width'],
                      height : windowSizeStore.getWindowSize['height'] }
    //console.log("*** REP TREE new window size",windowSizeRef.value)
    updateWidthHeights()
    menuTrigger.value++
  })
  
  const openErrorModal = async (programName,errorMessage) => {
    const modal = await modalController.create({
      component: ModalErrorMessage,
      componentProps: {
        type : "commError",
        program : programName ,
        errorMessage : errorMessage
      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();
  };

  function boardMove(arrayIndex,from,to) {
  }

  function moveClick(newFen) {
    // clear arrows on a regular move click
    arrowPairs.value = []
    fen.value = newFen
  }

  function branchClick(arrows) {
    //console.log("branch click arrows -> ",arrows)
    arrowPairs.value = []
    for (var i=0; i < arrows.length; i++)
      arrowPairs.value.push(arrows[i])
  }

  // WORK - coming from reptree

  function toolbarUpdate(backDisabled,forwardDisabled) {
    //console.log("toolbar update  ",backDisabled,forwardDisabled)
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_BACK,backDisabled)
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_FORWARD,forwardDisabled)
  
    toolbarTrigger.value++
  }

  function toolbarClick(index) {

    // trap that the button should be disabled but the screen hasn't caught up
    var element = findToolbarElement(toolbarElements.value,index)
    if (element != null && element.disabled)
      return
      
    switch (index) {
      case TOOLBAR_BACK :
        directionTriggerCounter++
        directionTrigger.value = -directionTriggerCounter
        break;
      case TOOLBAR_FORWARD :
        directionTriggerCounter++
        directionTrigger.value = directionTriggerCounter
        break;
      case TOOLBAR_COMMENTS :
        showComments.value = !showComments.value
        setToolbarElementLabel(toolbarElements.value,TOOLBAR_COMMENTS,
                              (showComments.value) ? t("Hide Comments") : t("Show Comments"))
        toolbarTrigger.value++
        break
      case TOOLBAR_REPERTOIRE :
        repertoireSelectorModal()
        break
    }
  }

  function tourDone() {
    showTour.value = false
  }

  function repertoireDone(didChange) {
    //console.log("rep changed ",didChange)

    // reset board
    incrementAppendTrigger()
    fen.value = INITIAL_FEN + blankAppendTrigger
    arrowPairs.value = []

    showState.value = SHOW_TREE

    if (didChange) {
      reloadTrigger.value++
    }
    //console.log("in rep ",didChange)
    //if (didChange)
    //  buildPracticeLines()
  }
  
</script>

<style scoped>

#reptree-container {
  position : relative;
}

</style>